'use client';

import React from 'react';

// Libraries
import LogRocket from 'logrocket';
import Image from 'next/legacy/image';

// MUI
import {
    Button
    , Stack
    , ThemeProvider
    , Typography
} from '@mui/material';
import { themeV2 } from '@/theme/theme-v2';

// Utils
import { pxrem } from '@/utils/pxrem';
import { logRocketException } from '@/utils/logrocket';
import { openChat } from '@/utils/intercom';
import {
    isChunkLoadError
    , reloadForChunkError
} from '@/utils/chunkLoadError';

export interface Props {
    error: Error;
    resetErrorBoundary: () => void;
    showBackground?: boolean;
}

const ErrorFallback = ( {
    error
    , resetErrorBoundary
    , showBackground = false
}: Props ) => {
    const isChunkError = isChunkLoadError( error );
    const [ isReloading, setIsReloading ] = React.useState( false );

    console.error( `Error Fallback ${ error.message }` );

    LogRocket.captureException(
        logRocketException( error, 'Component Error Fallback' )
        , {
            tags: {
                component: 'Error Fallback'
                , chunkLoadError: isChunkError
            }
        }
    );

    const handleContact = React.useCallback( () => {
        openChat();
    }, [] );

    React.useEffect( () => {
        if ( isChunkError ) {
            setIsReloading( reloadForChunkError() );
            return;
        }

        openChat();
    }, [ isChunkError ] );

    if ( isReloading ) {
        return null;
    }

    return (
        <ThemeProvider theme={ themeV2 }>
            <Stack
                sx={ {
                    gap: pxrem( 16 )
                    , paddingY: pxrem( 30 )
                    , justifyContent: 'center'
                    , alignItems: 'center'
                    , width: '100%'
                    , backgroundColor: theme => showBackground ? theme.palette.common.white : undefined
                } }
            >
                <Image
                    src='error-fallback-image.png'
                    alt='Error Fallback Banner'
                    width={ 215 }
                    height={ 183 }
                />
                <Typography
                    variant='h3'
                    sx={ {
                        marginTop: pxrem( 16 )
                        , color: theme => theme.palette.gray.secondary
                    } }
                >
                    { isChunkError ? 'New version available' : 'Oops!' }
                </Typography>
                <Typography
                    variant='body1'
                    sx={ {
                        textAlign: 'center'
                        , marginTop: pxrem( -4 )
                        , maxWidth: pxrem( 400 )
                        , color: theme => theme.palette.gray.secondary
                    } }
                >
                    {
                        isChunkError
                            ? 'We\'ve updated the Business Portal. Please close this tab and open a new one to load the latest version.'
                            : 'Something went wrong on our end and we encountered an error. Please try again.'
                    }
                </Typography>
                <Button
                    variant='contained'
                    color='primary'
                    onClick={ () => {
                        if ( !window ) {
                            return;
                        }

                        if ( isChunkError ) {
                            window.open( window.location.href, '_blank' );
                            return;
                        }

                        window.location.reload();
                    } }
                >
                    { isChunkError ? 'Open New Tab' : 'Retry' }
                </Button>
                {
                    window.Intercom
                        ? (
                            <Button
                                variant='text'
                                color='primary'
                                onClick={ handleContact }
                            >
                            Chat with Support
                            </Button>
                        )
                        : (
                            <Button
                                variant='text'
                                color='primary'
                                onClick={ () => {
                                    if ( window ) {
                                        window.location.href = 'mailto:support@veryableops.com';
                                    }
                                } }
                            >
                                Email Support
                            </Button>
                        )
                }
            </Stack>
        </ThemeProvider>
    );
};

export default ErrorFallback;
